import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { PartnerOnboardingService } from "../../../api/generated/services/PartnerOnboardingService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface RejectApplicationModalProps {
  isOpen: boolean;
  applicationId: string;
  companyName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RejectApplicationModal: React.FC<RejectApplicationModalProps> = ({
  isOpen,
  applicationId,
  companyName,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!applicationId) {
      setError("Application ID is missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await PartnerOnboardingService.postApiPartnerOnboardingRejectApplication({
        partnerApplicationId: applicationId,
      });
      // Reset state and close modal directly (bypass handleClose's loading check)
      setError(null);
      setLoading(false);
      onClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error rejecting application:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to reject application. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <Modal
      className="modal-content"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Reject Application"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Reject Application</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
          <p className="text-sm text-orange-800">
            Are you sure you want to reject the partner application for{" "}
            <span className="font-medium">{companyName}</span>?
          </p>
          <p className="text-sm text-orange-800 mt-2">
            The application will be moved to the rejected list. You can still approve it later if needed.
          </p>
        </div>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            isLoading={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Reject
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RejectApplicationModal;
